'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import type { BurgerMenuNavigationLoaderData } from './burger-menu-loader-data';
import { parseCategorySubmenuSlots, type CategorySubmenuSlots } from './category-submenu-slots';
import { hydrateEmbeddedComponentData } from './embedded-component';
import { parseMenuItemListOverrides, type MenuItemListOverrides } from './menu-item-list-overrides';

export const BURGER_MENU_NAVIGATION_RESOURCE = '/resource/burger-menu-navigation';

type UseBurgerMenuContentResult = {
    subCategoriesPromise: Promise<ShopperProducts.schemas['Category'][]> | undefined;
    burgerMenuEmbedded: ComponentWithComponentData | undefined;
    menuOverrides: MenuItemListOverrides;
    categorySubmenuSlots: CategorySubmenuSlots;
    isContentSlotsLoading: boolean;
    hasLoaded: boolean;
};

export function useBurgerMenuContent(open: boolean): UseBurgerMenuContentResult {
    const fetcher = useFetcher<BurgerMenuNavigationLoaderData>({ key: 'burger-menu-navigation' });
    const subsDeferredResolveRef = useRef<((subs: ShopperProducts.schemas['Category'][]) => void) | null>(null);
    const [subCategoriesPromise, setSubCategoriesPromise] = useState<
        Promise<ShopperProducts.schemas['Category'][]> | undefined
    >();

    useEffect(() => {
        if (!open || fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        void fetcher.load(BURGER_MENU_NAVIGATION_RESOURCE);
    }, [open, fetcher, fetcher.data, fetcher.state, fetcher.load]);

    useEffect(() => {
        if (fetcher.data?.subs && subsDeferredResolveRef.current) {
            subsDeferredResolveRef.current(fetcher.data.subs);
            subsDeferredResolveRef.current = null;
        }
    }, [fetcher.data?.subs]);

    useEffect(() => {
        if (!open) {
            setSubCategoriesPromise(undefined);
            subsDeferredResolveRef.current = null;
            return;
        }

        if (fetcher.data?.subs) {
            setSubCategoriesPromise(Promise.resolve(fetcher.data.subs));
            subsDeferredResolveRef.current = null;
            return;
        }

        setSubCategoriesPromise(
            new Promise<ShopperProducts.schemas['Category'][]>((resolve) => {
                subsDeferredResolveRef.current = resolve;
            })
        );
    }, [open, fetcher.data?.subs]);

    const isContentSlotsLoading = open && !fetcher.data;
    const burgerMenuEmbedded = useMemo(
        () =>
            fetcher.data?.burgerMenuEmbedded
                ? hydrateEmbeddedComponentData(fetcher.data.burgerMenuEmbedded)
                : undefined,
        [fetcher.data?.burgerMenuEmbedded]
    );

    const menuOverrides = useMemo(
        () => (burgerMenuEmbedded ? parseMenuItemListOverrides(burgerMenuEmbedded) : {}),
        [burgerMenuEmbedded]
    );

    const categorySubmenuSlots = useMemo(
        () => (burgerMenuEmbedded ? parseCategorySubmenuSlots(burgerMenuEmbedded) : {}),
        [burgerMenuEmbedded]
    );

    return useMemo(
        () => ({
            subCategoriesPromise,
            burgerMenuEmbedded,
            menuOverrides,
            categorySubmenuSlots,
            isContentSlotsLoading,
            hasLoaded: Boolean(fetcher.data),
        }),

        [
            subCategoriesPromise,
            burgerMenuEmbedded,
            menuOverrides,
            categorySubmenuSlots,
            isContentSlotsLoading,
            fetcher.data,
        ]
    );
}
